import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/es";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Navigate, useNavigate } from "react-router-dom";
import { useUser } from "../../context/UserConext";
import { useSnackbar } from "../../context/SnackbarContext";
import {
  TangoOrder,
  tangoOrderService,
} from "../../services/order/tangoOrderService";

const defaultDates = () => ({
  from: dayjs().subtract(10, "day").startOf("day"),
  to: dayjs().startOf("day"),
});
type DateRange = { from: Dayjs | null; to: Dayjs | null };
const amount = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
});

export default function TangoOrdersPage() {
  const navigate = useNavigate();
  const { user } = useUser();
  const { showSnackbar } = useSnackbar();
  const isAdmin = user?.user?.permission?.includes("VIEW_ADMIN");
  const [dates, setDates] = useState<DateRange>(defaultDates);
  const [dateErrors, setDateErrors] = useState({ from: false, to: false });
  const [filters, setFilters] = useState(() => ({
    from: dates.from!.format("YYYY-MM-DD"),
    to: dates.to!.format("YYYY-MM-DD"),
  }));
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [orders, setOrders] = useState<TangoOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // if (!isAdmin) return;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    setOrders([]);
    tangoOrderService
      .getOrders(page, pageSize, filters.from, filters.to, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setOrders(data.orders);
        setTotal(data.total);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setTotal(0);
        setError(
          err.response?.data?.message || "No se pudieron cargar los pedidos.",
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [page, pageSize, filters, isAdmin]);

  // if (!isAdmin) return <Navigate to="/home" replace />;

  const fromValid = !!dates.from?.isValid() && !dateErrors.from;
  const toValid = !!dates.to?.isValid() && !dateErrors.to;
  const invertedDates = fromValid && toValid && dates.from!.isAfter(dates.to, "day");
  const invalidDates = !fromValid || !toValid || invertedDates;
  const visibleSelected = orders.filter((order) =>
    selected.has(order.orderId),
  ).length;
  const busy = loading || exporting;

  const toggle = (ids: string[], checked: boolean) => {
    setSelected((previous) => {
      const next = new Set(previous);
      ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
      return next;
    });
  };

  const applyFilters = (value = dates) => {
    if (!value.from?.isValid() || !value.to?.isValid() || value.from.isAfter(value.to, "day")) return;
    setPage(0);
    setSelected(new Set());
    setFilters({ from: value.from.format("YYYY-MM-DD"), to: value.to.format("YYYY-MM-DD") });
  };

  const download = async () => {
    setExporting(true);
    try {
      const blob = await tangoOrderService.exportOrders(Array.from(selected));
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `Pedidos_Tango_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 10000);
      showSnackbar(
        "Excel generado. Importalo manualmente desde TANGO Gestión.",
        "success",
        6000,
      );
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : "No se pudo exportar el Excel.",
        "error",
        8000,
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Stack
          direction="row"
          alignItems="center"
          gap={2}
          mb={3}
          flexWrap="wrap"
        >
          <Typography variant="h5" color="primary" sx={{ flexGrow: 1 }}>
            Exportación de pedidos a TANGO
          </Typography>
        </Stack>
        <Box
          component="form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!invalidDates) applyFilters();
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <Stack
              direction={{ xs: "column", sm: "row" }}
              gap={2}
              mb={2}
              alignItems={{ sm: "center" }}
            >
              <DatePicker
                label="Fecha desde"
                format="DD/MM/YYYY"
                value={dates.from}
                disabled={exporting}
                onChange={(value) =>
                  setDates((previous) => ({ ...previous, from: value }))
                }
                onError={(error) => setDateErrors((previous) => ({ ...previous, from: error !== null }))}
                slotProps={{ textField: {
                  size: "small",
                  error: !fromValid || invertedDates,
                  helperText: !fromValid ? "Ingresá una fecha desde válida." : invertedDates ? "Fecha desde debe ser menor o igual que fecha hasta." : undefined,
                } }}
              />
              <DatePicker
                label="Fecha hasta"
                format="DD/MM/YYYY"
                value={dates.to}
                disabled={exporting}
                onChange={(value) =>
                  setDates((previous) => ({ ...previous, to: value }))
                }
                onError={(error) => setDateErrors((previous) => ({ ...previous, to: error !== null }))}
                slotProps={{ textField: {
                  size: "small",
                  error: !toValid || invertedDates,
                  helperText: !toValid ? "Ingresá una fecha hasta válida." : invertedDates ? "Fecha hasta debe ser mayor o igual que fecha desde." : undefined,
                } }}
              />
              <Button
                type="submit"
                variant="outlined"
                disabled={busy || invalidDates}
              >
                Filtrar
              </Button>
              <Button
                disabled={busy}
                onClick={() => {
                  const value = defaultDates();
                  setDateErrors({ from: false, to: false });
                  setDates(value);
                  applyFilters(value);
                }}
              >
                Últimos 10 días
              </Button>
            </Stack>
          </LocalizationProvider>
        </Box>
        {invalidDates && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Ingresá un rango de fechas válido.
          </Alert>
        )}
        <Stack
          direction="row"
          alignItems="center"
          gap={2}
          mb={2}
          flexWrap="wrap"
        >
          <Typography sx={{ flexGrow: 1 }}>
            {selected.size} pedido(s) seleccionado(s)
          </Typography>
          <Button
            disabled={busy || selected.size === 0}
            onClick={() => setSelected(new Set())}
          >
            Limpiar selección
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={busy || selected.size === 0 || selected.size > 1000}
            startIcon={
              exporting ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <DownloadIcon />
              )
            }
            onClick={download}
          >
            {exporting ? "Generando Excel…" : "Exportar Excel"}
          </Button>
        </Stack>
        {selected.size > 1000 && (
          <Alert severity="warning">
            Exportá hasta 1000 pedidos por archivo.
          </Alert>
        )}
        {exporting && (
          <Box role="status" aria-live="polite" mb={2}>
            <Typography variant="body2">Preparando descarga…</Typography>
            <LinearProgress />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading && <LinearProgress aria-label="Cargando pedidos" />}
        <TableContainer>
          <Table aria-label="Pedidos enviados para exportar a TANGO">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    disabled={busy || orders.length === 0}
                    checked={
                      orders.length > 0 && visibleSelected === orders.length
                    }
                    indeterminate={
                      visibleSelected > 0 && visibleSelected < orders.length
                    }
                    onChange={(_, checked) =>
                      toggle(
                        orders.map((order) => order.orderId),
                        checked,
                      )
                    }
                    inputProps={{
                      "aria-label":
                        "Seleccionar todos los pedidos de esta página",
                    }}
                  />
                </TableCell>
                <TableCell>Nro. correlativo</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Fecha de creación</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell align="center">Acción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow
                  key={order.orderId}
                  selected={selected.has(order.orderId)}
                  hover
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      disabled={busy}
                      checked={selected.has(order.orderId)}
                      onChange={(_, checked) =>
                        toggle([order.orderId], checked)
                      }
                      inputProps={{
                        "aria-label": `Seleccionar pedido ${order.consecutiveNumber}`,
                      }}
                    />
                  </TableCell>
                  <TableCell>{order.consecutiveNumber}</TableCell>
                  <TableCell align="right">
                    {amount.format(order.total)}
                  </TableCell>
                  <TableCell>
                    {dayjs(order.createDate).format("DD/MM/YYYY HH:mm")}
                  </TableCell>
                  <TableCell>{order.clientName}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver pedido">
                      <IconButton color="primary" aria-label={`Ver pedido ${order.consecutiveNumber}`}
                        disabled={exporting}
                        onClick={() => navigate(`/orders/tango/${order.orderId}`)}>
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && !error && orders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No hay pedidos enviados en este período.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[10, 25, 50, 100]}
          disabled={busy}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} de ${count}`
          }
          onPageChange={(_, value) => setPage(value)}
          onRowsPerPageChange={(event) => {
            setPageSize(Number(event.target.value));
            setPage(0);
          }}
        />
      </CardContent>
    </Card>
  );
}
